/* ==========================================================================
   Accept Invitation Page
   Task F12/F19/F24a: Initial password setup with auto-login
   Task F30: Updated to use pharmacy colors and PharmacyLogo
   ========================================================================== */

import { useState } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, CheckCircle } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { Alert } from '@/components/ui/Alert'
import { PharmacyLogo } from '@/components/ui/PharmacyLogo'
import { acceptInvitation } from '@/api/auth'
import { ApiClientError } from '@/api/client'
import { usePortalSlug } from '@/context/PortalContext'
import { useAuth } from '@/hooks/useAuth'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface FormErrors {
  password?: string
  confirmPassword?: string
}

/* --------------------------------------------------------------------------
   Password Requirements
   -------------------------------------------------------------------------- */

interface PasswordRequirement {
  label: string
  test: (password: string) => boolean
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'At least one uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'At least one lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'At least one number', test: (p) => /\d/.test(p) },
]

/* --------------------------------------------------------------------------
   Validation
   -------------------------------------------------------------------------- */

function validatePassword(password: string): string | undefined {
  if (!password) {
    return 'Password is required'
  }
  if (password.length < 8) {
    return 'Password must be at least 8 characters'
  }
  if (new TextEncoder().encode(password).length > 72) {
    return 'Password must be at most 72 characters (fewer if using non-English characters or emoji)'
  }
  if (!/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter'
  }
  if (!/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter'
  }
  if (!/\d/.test(password)) {
    return 'Password must contain at least one number'
  }
  return undefined
}

function validateConfirmPassword(
  password: string,
  confirmPassword: string
): string | undefined {
  if (!confirmPassword) {
    return 'Please confirm your password'
  }
  if (password !== confirmPassword) {
    return 'Passwords do not match'
  }
  return undefined
}

function validateForm(password: string, confirmPassword: string): FormErrors {
  return {
    password: validatePassword(password),
    confirmPassword: validateConfirmPassword(password, confirmPassword),
  }
}

/* --------------------------------------------------------------------------
   Password Requirements Display
   -------------------------------------------------------------------------- */

function PasswordRequirements({ password }: { password: string }) {
  return (
    <div className="mt-2 space-y-1">
      {PASSWORD_REQUIREMENTS.map((req, index) => {
        const met = req.test(password)
        return (
          <div
            key={index}
            className={`flex items-center gap-2 text-sm transition-colors ${
              met ? 'text-green-600' : 'text-charcoal/50'
            }`}
          >
            <CheckCircle
              className={`h-4 w-4 ${met ? 'opacity-100' : 'opacity-30'}`}
              aria-hidden="true"
            />
            <span>{req.label}</span>
          </div>
        )
      })}
    </div>
  )
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function AcceptInvitation() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const slug = usePortalSlug()
  const { loginWithToken } = useAuth()

  // Form state
  const [password, setPasswordValue] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState<string | undefined>()

  // Handle field blur
  const handleBlur = (field: 'password' | 'confirmPassword') => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    if (field === 'password') {
      setErrors((prev) => ({ ...prev, password: validatePassword(password) }))
    } else {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: validateConfirmPassword(password, confirmPassword),
      }))
    }
  }

  // Handle password change
  const handlePasswordChange = (value: string) => {
    setPasswordValue(value)
    setApiError(undefined)
    if (touched.password) {
      setErrors((prev) => ({ ...prev, password: validatePassword(value) }))
    }
    // Re-validate confirm if it's been touched
    if (touched.confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: validateConfirmPassword(value, confirmPassword),
      }))
    }
  }

  // Handle confirm password change
  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value)
    setApiError(undefined)
    if (touched.confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: validateConfirmPassword(password, value),
      }))
    }
  }

  // Handle form submission
  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!token) {
      return
    }

    // Validate all fields
    const formErrors = validateForm(password, confirmPassword)
    setErrors(formErrors)
    setTouched({ password: true, confirmPassword: true })

    // Check for validation errors
    if (formErrors.password || formErrors.confirmPassword) {
      return
    }

    setLoading(true)
    setApiError(undefined)

    try {
      // Accept invitation returns a token for auto-login
      const response = await acceptInvitation({ token, password })

      // Auto-login with the returned token
      await loginWithToken(response.access_token, response.expires_at)

      // Redirect to profile to complete setup
      navigate(`/rx/${slug}/profile`, { replace: true })
    } catch (err) {
      if (err instanceof ApiClientError) {
        setApiError(err.getUserMessage())
      } else {
        setApiError('An unexpected error occurred. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  // No token provided
  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
        <div className="w-full max-w-md">
          {/* Pharmacy Logo (Task F30) */}
          <div className="flex justify-center mb-8">
            <PharmacyLogo size="xl" linkTo="/" />
          </div>

          <Card className="p-6 sm:p-8">
            <Alert variant="error" className="mb-6">
              No invitation token provided. Please use the link from your
              invitation email.
            </Alert>
            <Link to={`/rx/${slug}/login`}>
              <Button variant="primary" className="w-full">
                Go to Login
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    )
  }

  // Form state
  const hasErrors = errors.password || errors.confirmPassword

  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
      <div className="w-full max-w-md">
        {/* Pharmacy Logo (Task F30) */}
        <div className="flex justify-center mb-8">
          <PharmacyLogo size="xl" linkTo="/" />
        </div>

        <Card className="p-6 sm:p-8">
          {/* Title */}
          <h1 className="font-serif text-2xl sm:text-3xl text-charcoal text-center mb-2">
            Set your password
          </h1>

          {/* Subtitle */}
          <p className="text-center text-charcoal/70 mb-6">
            Create a secure password for your account.
          </p>

          {/* API error alert */}
          {apiError && (
            <Alert variant="error" className="mb-6">
              {apiError}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-5">
              {/* Password field */}
              <div>
                <FormField
                  label="Password"
                  required
                  error={touched.password ? errors.password : undefined}
                  htmlFor="password"
                >
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      maxLength={72}
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => handlePasswordChange(e.target.value)}
                      onBlur={() => handleBlur('password')}
                      error={touched.password && !!errors.password}
                      disabled={loading}
                      className="pr-12"
                      aria-describedby={
                        errors.password ? 'password-error' : 'password-reqs'
                      }
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-charcoal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pharmacy-secondary)] rounded"
                      aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                      }
                      disabled={loading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" aria-hidden="true" />
                      ) : (
                        <Eye className="h-5 w-5" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </FormField>
                {/* Password requirements */}
                <div id="password-reqs">
                  <PasswordRequirements password={password} />
                </div>
              </div>

              {/* Confirm password field */}
              <FormField
                label="Confirm Password"
                required
                error={
                  touched.confirmPassword ? errors.confirmPassword : undefined
                }
                htmlFor="confirmPassword"
              >
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    maxLength={72}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) =>
                      handleConfirmPasswordChange(e.target.value)
                    }
                    onBlur={() => handleBlur('confirmPassword')}
                    error={touched.confirmPassword && !!errors.confirmPassword}
                    disabled={loading}
                    className="pr-12"
                    aria-describedby={
                      errors.confirmPassword
                        ? 'confirmPassword-error'
                        : undefined
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-charcoal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pharmacy-secondary)] rounded"
                    aria-label={
                      showConfirmPassword ? 'Hide password' : 'Show password'
                    }
                    disabled={loading}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <Eye className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </FormField>
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              disabled={loading || !!hasErrors}
              className="w-full mt-6"
            >
              Set password
            </Button>
          </form>

          {/* Login link */}
          <div className="mt-6 text-center">
            <span className="text-sm text-charcoal/60">
              Already have a password?{' '}
            </span>
            <Link
              to={`/rx/${slug}/login`}
              className="text-sm text-[var(--pharmacy-primary)] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pharmacy-secondary)] rounded"
            >
              Sign in
            </Link>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default AcceptInvitation
